import React, { useEffect, useState } from "react";
import { useAuth } from "../context/AuthContext";
import { Link, useNavigate } from "react-router-dom";
import { db } from "../firebase";
import { ref, get, set, push, remove } from "firebase/database";
import { uploadImage } from "../utils/uploadImage";
import { ShieldCheck, Shield, Star, Package, LogOut, ChevronRight, ArrowLeftRight, FileText, Bell, Users, Pencil, X, Check, Heart, MessageCircle, Copy, Camera, TrendingUp, Mail, CheckCircle, Lock, Eye, EyeOff, Trophy, History, Trash2, AlertTriangle, HelpCircle, CalendarDays } from "lucide-react";
import { auth } from "../firebase";
import { reauthenticateWithCredential, EmailAuthProvider, updatePassword } from "firebase/auth";

export default function Profile() {
  const { currentUser, userProfile, logout, updateUserProfile, deleteAccount } = useAuth();
  const navigate = useNavigate();
  const [stats, setStats] = useState({ active: 0, sold: 0 });
  const [editing, setEditing] = useState(false);
  const [newPhone, setNewPhone] = useState("");
  const [newName, setNewName] = useState("");
  const [newCollege, setNewCollege] = useState("");
  const [otherCollegeInput, setOtherCollegeInput] = useState("");
  const [collegeTicketMsg, setCollegeTicketMsg] = useState("");
  const [collegeTicketSent, setCollegeTicketSent] = useState(false);
  const [saving, setSaving] = useState(false);
  const [saved, setSaved] = useState(false);
  const [upiId, setUpiId] = useState("");
  const [savedListings, setSavedListings] = useState([]);
  const [referralCopied, setReferralCopied] = useState(false);
  const [uploadingPhoto, setUploadingPhoto] = useState(false);
  const [reviews, setReviews] = useState([]);
  // College email verification
  const [collegeEmail, setCollegeEmail] = useState("");
  const [emailVerifying, setEmailVerifying] = useState(false);
  const [showEmailVerify, setShowEmailVerify] = useState(false);

  const [showMenu, setShowMenu] = useState(false);
  const [showPhotoViewer, setShowPhotoViewer] = useState(false);

  // Photo privacy
  const [showPrivacySection, setShowPrivacySection] = useState(false);
  const [photoPrivacy, setPhotoPrivacy] = useState("everyone");
  const [savingPrivacy, setSavingPrivacy] = useState(false);
  const [privacySaved, setPrivacySaved] = useState(false);
  const [photoAllowList, setPhotoAllowList] = useState({}); // { uid: { name, photoURL } }
  const [allowSearch, setAllowSearch] = useState("");
  const [allowSearchResults, setAllowSearchResults] = useState([]);
  const [searchingAllow, setSearchingAllow] = useState(false);

  // Help & Support / Feedback
  const [showFeedback, setShowFeedback] = useState(false);
  const [feedbackType, setFeedbackType] = useState("Bug Report");
  const [feedbackMsg, setFeedbackMsg] = useState("");
  const [feedbackSubmitting, setFeedbackSubmitting] = useState(false);
  const [feedbackDone, setFeedbackDone] = useState(false);
  const [feedbackError, setFeedbackError] = useState("");

  async function submitFeedback(e) {
    e.preventDefault();
    if (!feedbackMsg.trim()) return;
    setFeedbackSubmitting(true);
    setFeedbackError("");
    try {
      await push(ref(db, `feedback/${currentUser.uid}`), {
        type: feedbackType,
        message: feedbackMsg.trim(),
        userName: userProfile?.name || "Unknown",
        userEmail: currentUser.email || "",
        college: userProfile?.college || userProfile?.childSchool || "",
        createdAt: Date.now(),
        status: "open",
      });
      setFeedbackMsg("");
      setFeedbackDone(true);
      setTimeout(() => { setFeedbackDone(false); setShowFeedback(false); }, 2500);
    } catch (err) {
      setFeedbackError("Failed to send. Please try again or email campusswap.official@gmail.com");
    }
    setFeedbackSubmitting(false);
  }

  function startEdit() {
    setNewPhone(userProfile?.phone || "");
    setNewName(userProfile?.name || "");
    setUpiId(userProfile?.upiId || "");
    setEditing(true);
  }

  async function submitCollegeTicket() {
    if (!collegeTicketMsg.trim()) return;
    await push(ref(db, "feedback/" + currentUser.uid), {
      type: "College Change Request",
      message: collegeTicketMsg.trim(),
      userName: userProfile?.name || "",
      userEmail: currentUser.email || "",
      college: userProfile?.college || "",
      createdAt: Date.now(),
      status: "open",
    });
    setCollegeTicketSent(true);
    setCollegeTicketMsg("");
  }

  async function saveEdit() {
    if (!newPhone.trim() || !newName.trim()) return;
    setSaving(true);
    const updates = { phone: newPhone.trim(), name: newName.trim(), upiId: upiId.trim() };
    const collegeChanges = userProfile?.collegeChanges || 0;
    const collegeApproved = userProfile?.collegeChangeApproved || false;
    const finalCollege = newCollege === "Other" ? otherCollegeInput.trim() : newCollege;
    if (finalCollege && finalCollege !== userProfile?.college) {
      if (collegeChanges < 2 || collegeApproved) {
        updates.college = finalCollege;
        updates.collegeChanges = collegeChanges + 1;
        if (collegeApproved) updates.collegeChangeApproved = false;
      }
    }
    await updateUserProfile(currentUser.uid, updates);
    setSaving(false);
    setSaved(true);
    setEditing(false);
    setTimeout(() => setSaved(false), 2000);
  }

  useEffect(() => {
    if (!currentUser) return;
    async function fetchStats() {
      try {
        const snap = await get(ref(db, "listings"));
        if (snap.exists()) {
          const items = Object.values(snap.val()).filter(l => l.sellerId === currentUser.uid);
          setStats({ active: items.filter(i => i.status === "active").length, sold: items.filter(i => i.status === "sold").length });
        }
      } catch (e) {  }
    }
    async function fetchSaved() {
      try {
        const savedSnap = await get(ref(db, `saved/${currentUser.uid}`));
        if (savedSnap.exists()) {
          const savedIds = Object.keys(savedSnap.val());
          const listingsSnap = await get(ref(db, "listings"));
          if (listingsSnap.exists()) {
            const all = listingsSnap.val();
            const items = savedIds.map(id => all[id] ? { id, ...all[id] } : null).filter(Boolean);
            setSavedListings(items);
          }
        }
      } catch (e) {  }
    }
    async function fetchReviews() {
      try {
        const revSnap = await get(ref(db, `reviews/${currentUser.uid}`));
        if (revSnap.exists()) {
          const revs = Object.entries(revSnap.val()).map(([id, r]) => ({ id, ...r }));
          setReviews(revs.sort((a, b) => b.createdAt - a.createdAt).slice(0, 5));
        }
      } catch (e) {  }
    }
    fetchStats();
    fetchSaved();
    fetchReviews();
  }, [currentUser]);

  // Load photo privacy settings when profile loads
  useEffect(() => {
    if (!userProfile) return;
    setPhotoPrivacy(userProfile.photoPrivacy || "everyone");
    // Load allow list if custom mode
    async function loadAllowList() {
      if ((userProfile.photoPrivacy || "everyone") !== "custom") return;
      try {
        const snap = await get(ref(db, `users/${currentUser.uid}/photoAllowList`));
        if (snap.exists()) {
          const uids = Object.keys(snap.val());
          const users = {};
          await Promise.all(uids.map(async (uid) => {
            try {
              const uSnap = await get(ref(db, `users/${uid}`));
              if (uSnap.exists()) {
                const d = uSnap.val();
                users[uid] = { name: d.name || "User", photoURL: d.photoURL || null };
              }
            } catch {}
          }));
          setPhotoAllowList(users);
        }
      } catch {}
    }
    loadAllowList();
  }, [userProfile, currentUser]); // eslint-disable-line

  async function savePrivacy(newPrivacy) {
    setSavingPrivacy(true);
    try {
      await updateUserProfile(currentUser.uid, { photoPrivacy: newPrivacy });
      setPhotoPrivacy(newPrivacy);
      setPrivacySaved(true);
      setTimeout(() => setPrivacySaved(false), 2000);
    } catch {}
    setSavingPrivacy(false);
  }

  async function searchAllowUsers(query) {
    if (!query.trim() || query.trim().length < 2) { setAllowSearchResults([]); return; }
    setSearchingAllow(true);
    try {
      const snap = await get(ref(db, "users"));
      if (!snap.exists()) { setAllowSearchResults([]); return; }
      const q = query.trim().toLowerCase();
      const results = Object.entries(snap.val())
        .filter(([uid, u]) => {
          if (uid === currentUser.uid) return false;
          if (photoAllowList[uid]) return false; // already added
          return (u.name || "").toLowerCase().includes(q) || (u.college || "").toLowerCase().includes(q);
        })
        .slice(0, 8)
        .map(([uid, u]) => ({ uid, name: u.name || "User", college: u.college || "", photoURL: u.photoURL || null }));
      setAllowSearchResults(results);
    } catch {}
    setSearchingAllow(false);
  }

  async function addToAllowList(user) {
    try {
      await set(ref(db, `users/${currentUser.uid}/photoAllowList/${user.uid}`), true);
      setPhotoAllowList(prev => ({ ...prev, [user.uid]: { name: user.name, photoURL: user.photoURL } }));
      setAllowSearch("");
      setAllowSearchResults([]);
    } catch {}
  }

  async function removeFromAllowList(uid) {
    try {
      await remove(ref(db, `users/${currentUser.uid}/photoAllowList/${uid}`));
      setPhotoAllowList(prev => { const n = { ...prev }; delete n[uid]; return n; });
    } catch {}
  }

  async function handlePhotoUpload(e) {
    const file = e.target.files[0];
    if (!file) return;
    if (file.size > 5 * 1024 * 1024) { alert("Image must be under 5MB."); return; }
    try {
      setUploadingPhoto(true);
      const url = await uploadImage(file);
      await updateUserProfile(currentUser.uid, { photoURL: url });
    } catch (err) {
      alert("Failed to upload photo. Try again.");
    } finally { setUploadingPhoto(false); }
  }

  const COLLEGE_DOMAINS = [".edu", ".ac.in", ".edu.in", ".ac.uk", ".university"];

  async function verifyCollegeEmail() {
    if (!collegeEmail.trim()) return;
    const emailLower = collegeEmail.toLowerCase().trim();
    const isCollegeDomain = COLLEGE_DOMAINS.some(d => emailLower.endsWith(d));
    if (!isCollegeDomain) {
      alert("Please enter a valid college email (e.g., .edu, .ac.in, .ac.uk)");
      return;
    }
    setEmailVerifying(true);
    try {
      // Submit college email for admin review — cannot auto-verify without sending actual email
      await set(ref(db, `emailOTPs/${currentUser.uid}`), {
        email: emailLower,
        createdAt: Date.now(),
        verified: false,
      });
      await updateUserProfile(currentUser.uid, {
        collegeEmail: emailLower,
        collegeEmailVerified: false,
        idVerificationStatus: "pending",
      });
      setShowEmailVerify(false);
      alert(`College email submitted for review!\n\nOur team will verify your email (${emailLower}) within 24 hours and approve your account.`);
    } catch (e) {
      
      alert("Failed to submit. Please try again.");
    }
    setEmailVerifying(false);
  }

  async function handleLogout() {
    await logout();
    navigate("/login");
  }

  // Delete account
  const [showDeleteSection, setShowDeleteSection] = useState(false);
  const [deleteConfirmText, setDeleteConfirmText] = useState("");
  const [deletePassword, setDeletePassword] = useState("");
  const [deleteLoading, setDeleteLoading] = useState(false);
  const [deleteError, setDeleteError] = useState("");

  async function handleDeleteAccount(e) {
    e.preventDefault();
    setDeleteError("");
    if (deleteConfirmText !== "DELETE") { setDeleteError('Type DELETE to confirm.'); return; }
    setDeleteLoading(true);
    try {
      // Re-authenticate first (required by Firebase before account deletion)
      if (currentUser.email) {
        const credential = EmailAuthProvider.credential(currentUser.email, deletePassword);
        await reauthenticateWithCredential(auth.currentUser, credential);
      }
      await deleteAccount(currentUser.uid);
      navigate("/about");
    } catch (err) {
      if (err.code === "auth/wrong-password" || err.code === "auth/invalid-credential") setDeleteError("Incorrect password.");
      else if (err.code === "auth/requires-recent-login") setDeleteError("Please log out and log back in, then try again.");
      else setDeleteError("Failed to delete account. Please try again.");
    }
    setDeleteLoading(false);
  }

  // Change password
  const [showPwSection, setShowPwSection] = useState(false);
  const [pwForm, setPwForm] = useState({ current: "", newPw: "", confirm: "" });
  const [showPw, setShowPw] = useState({ current: false, newPw: false, confirm: false });
  const [pwError, setPwError] = useState("");
  const [pwSuccess, setPwSuccess] = useState(false);
  const [pwLoading, setPwLoading] = useState(false);

  const WEAK_PASSWORDS = new Set([
    "12345678","123456789","1234567890","password","password1","password123",
    "abcdefgh","qwerty123","qwertyui","iloveyou","india123","india@123","college1",
    "11111111","00000000","abc12345","pass1234","letmein1","master12",
  ]);
  function getPwStrength(pw) {
    if (!pw) return { score: 0, label: "", color: "" };
    if (pw.length < 8) return { score: 1, label: "Too short", color: "bg-red-500" };
    if (WEAK_PASSWORDS.has(pw.toLowerCase())) return { score: 1, label: "Too common", color: "bg-red-500" };
    if (/^(.)\1+$/.test(pw)) return { score: 1, label: "Too simple", color: "bg-red-500" };
    const score = [/[A-Z]/.test(pw), /[a-z]/.test(pw), /\d/.test(pw), /[^A-Za-z0-9]/.test(pw)].filter(Boolean).length;
    if (score <= 1) return { score: 2, label: "Weak", color: "bg-orange-400" };
    if (score === 2) return { score: 3, label: "Fair", color: "bg-yellow-400" };
    if (score === 3) return { score: 4, label: "Good", color: "bg-blue-500" };
    return { score: 5, label: "Strong", color: "bg-green-500" };
  }

  async function handleChangePassword(e) {
    e.preventDefault();
    setPwError(""); setPwSuccess(false);
    if (!pwForm.current) { setPwError("Enter your current password."); return; }
    if (pwForm.newPw.length < 8) { setPwError("New password must be at least 8 characters."); return; }
    const strength = getPwStrength(pwForm.newPw);
    if (strength.score <= 2) { setPwError("New password is too weak. Mix uppercase, lowercase, numbers and symbols."); return; }
    if (pwForm.newPw !== pwForm.confirm) { setPwError("Passwords do not match."); return; }
    setPwLoading(true);
    try {
      const credential = EmailAuthProvider.credential(currentUser.email, pwForm.current);
      await reauthenticateWithCredential(auth.currentUser, credential);
      await updatePassword(auth.currentUser, pwForm.newPw);
      setPwSuccess(true);
      setPwForm({ current: "", newPw: "", confirm: "" });
      setTimeout(() => { setPwSuccess(false); setShowPwSection(false); }, 3000);
    } catch (err) {
      if (err.code === "auth/wrong-password" || err.code === "auth/invalid-credential") setPwError("Current password is incorrect.");
      else if (err.code === "auth/too-many-requests") setPwError("Too many attempts. Try again later.");
      else setPwError("Failed to change password. Try again.");
    }
    setPwLoading(false);
  }

  const verificationStatus = userProfile?.idVerificationStatus;
  const isVerified = userProfile?.verified;
  const isParent = userProfile?.userType === "parent";

  return (
    <div className="pb-24 bg-gray-50 min-h-screen">
      {/* Header */}
      <div className={`bg-gradient-to-b ${isParent ? "from-amber-500 to-orange-500" : "from-blue-600 to-blue-700"} px-4 pt-4 pb-10 text-white`}>
        <div className="flex items-center gap-4">
          <div className="flex flex-col items-center gap-1" style={{ flexShrink: 0 }}>
            {/* WhatsApp-style profile photo */}
            <div style={{ position: "relative", width: 88, height: 88 }}>
              {/* White ring border */}
              <div style={{ position: "absolute", inset: -3, borderRadius: "9999px", background: "rgba(255,255,255,0.35)", zIndex: 0 }} />
              <div onClick={() => userProfile?.photoURL && setShowPhotoViewer(true)} style={{ position: "relative", width: 88, height: 88, borderRadius: "9999px", overflow: "hidden", border: "3px solid white", background: isParent ? "#fff7ed" : "#eff6ff", display: "flex", alignItems: "center", justifyContent: "center", fontSize: 36, fontWeight: 700, color: isParent ? "#d97706" : "#2563eb", zIndex: 1, cursor: userProfile?.photoURL ? "pointer" : "default" }}>
                {userProfile?.photoURL
                  ? <img src={userProfile.photoURL} alt="profile" style={{ width: "100%", height: "100%", objectFit: "cover" }} />
                  : userProfile?.name?.[0]?.toUpperCase() || "?"}
              </div>
              <label style={{ position: "absolute", bottom: 0, right: 0, width: 28, height: 28, borderRadius: "9999px", background: isParent ? "#f59e0b" : "#2563eb", border: "2px solid white", display: "flex", alignItems: "center", justifyContent: "center", cursor: "pointer", zIndex: 2 }}>
                {uploadingPhoto
                  ? <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin" />
                  : <Camera size={14} color="white" />}
                <input type="file" accept="image/*" onChange={handlePhotoUpload} style={{ display: "none" }} />
              </label>
            </div>
            <p className="text-white/60 text-xs mt-0.5">Tap to change</p>
          </div>
          <div className="flex-1">
            <div className="flex items-center gap-2 flex-wrap">
              <h2 className="text-xl font-bold">{userProfile?.name || (isParent ? "Parent" : "Student")}</h2>
              {isVerified && <ShieldCheck size={18} className="text-green-300" />}
              {isParent && (
                <span className="bg-white/20 text-white text-xs font-semibold px-2 py-0.5 rounded-full">Parent Account</span>
              )}
            </div>
            {isParent ? (
              <>
                <p className="text-orange-100 text-sm line-clamp-1">{userProfile?.childSchool || "School not set"}</p>
                <p className="text-orange-100 text-xs mt-0.5">{userProfile?.childClass} · {userProfile?.city}</p>
              </>
            ) : (
              <p className="text-blue-100 text-sm line-clamp-1">{userProfile?.college}</p>
            )}
            {userProfile?.totalRatings > 0 && (
              <div className="flex items-center gap-1 mt-0.5">
                <Star size={13} className="text-yellow-300 fill-yellow-300" />
                <span className="text-sm text-orange-100">{userProfile.rating?.toFixed(1)} ({userProfile.totalRatings} ratings)</span>
              </div>
            )}
          </div>
        </div>
      </div>

      <div className="px-4 -mt-5 space-y-3">
        {/* Profile Completion Bar */}
        {(() => {
          const checks = [
            { label: "Name", done: !!userProfile?.name },
            { label: "Phone", done: !!userProfile?.phone },
            { label: "Photo", done: !!userProfile?.photoURL },
            { label: "College", done: !!(userProfile?.college || userProfile?.childSchool) },
            { label: "ID Verified", done: userProfile?.idVerificationStatus === "approved" },
            { label: "UPI ID", done: !!userProfile?.upiId },
          ];
          const done = checks.filter(c => c.done).length;
          const pct = Math.round((done / checks.length) * 100);
          if (pct === 100) return null;
          return (
            <div className="bg-white rounded-2xl shadow-sm p-4">
              <div className="flex items-center justify-between mb-2">
                <p className="text-sm font-semibold text-gray-800">Profile Strength</p>
                <span className={`text-xs font-bold px-2 py-0.5 rounded-full ${pct >= 80 ? "bg-green-100 text-green-700" : pct >= 50 ? "bg-blue-100 text-blue-700" : "bg-orange-100 text-orange-700"}`}>{pct}%</span>
              </div>
              <div className="h-2 bg-gray-100 rounded-full overflow-hidden mb-2">
                <div className={`h-full rounded-full transition-all ${pct >= 80 ? "bg-green-500" : pct >= 50 ? "bg-blue-500" : "bg-orange-500"}`} style={{ width: `${pct}%` }} />
              </div>
              <div className="flex flex-wrap gap-1.5">
                {checks.filter(c => !c.done).map(c => (
                  <span key={c.label} className="text-xs bg-gray-100 text-gray-500 px-2 py-0.5 rounded-full">+ {c.label}</span>
                ))}
              </div>
            </div>
          );
        })()}

        {/* Stats */}
        <div className="bg-white rounded-2xl shadow-sm p-4">
          <div className="grid grid-cols-3 divide-x divide-gray-100">
            <div className="text-center pr-3">
              <p className="text-2xl font-bold text-blue-600">{stats.active}</p>
              <p className="text-xs text-gray-500 mt-0.5">Active</p>
            </div>
            <div className="text-center px-3">
              <p className="text-2xl font-bold text-green-600">{stats.sold}</p>
              <p className="text-xs text-gray-500 mt-0.5">Sold</p>
            </div>
            <div className="text-center pl-3">
              <p className="text-2xl font-bold text-yellow-500">{userProfile?.totalRatings || 0}</p>
              <p className="text-xs text-gray-500 mt-0.5">Ratings</p>
            </div>
          </div>
        </div>

        {/* Reviews Section */}
        {reviews.length > 0 && (
          <div className="bg-white rounded-2xl shadow-sm p-4">
            <div className="flex items-center gap-2 mb-3">
              <Star size={16} className="text-yellow-400 fill-yellow-400" />
              <p className="font-semibold text-gray-800 text-sm">
                Reviews ({userProfile?.totalRatings || 0})
                {userProfile?.rating > 0 && (
                  <span className="ml-2 text-yellow-600">{userProfile.rating.toFixed(1)} ⭐</span>
                )}
              </p>
            </div>
            <div className="space-y-3">
              {reviews.map(r => (
                <div key={r.id} className="border-b border-gray-50 pb-3 last:border-0 last:pb-0">
                  <div className="flex items-center gap-1 mb-1">
                    {[1,2,3,4,5].map(s => (
                      <Star key={s} size={11} className={s <= r.rating ? "text-yellow-400 fill-yellow-400" : "text-gray-200 fill-gray-200"} />
                    ))}
                    <span className="text-xs text-gray-400 ml-1">{new Date(r.createdAt).toLocaleDateString("en-IN", { day:"numeric", month:"short" })}</span>
                  </div>
                  {r.comment && <p className="text-xs text-gray-600 italic">"{r.comment}"</p>}
                  <p className="text-xs text-gray-400 mt-0.5">for: {r.listingTitle}</p>
                </div>
              ))}
            </div>
          </div>
        )}

        {/* Edit Profile */}
        <div className="bg-white rounded-2xl shadow-sm p-4">
          <div className="flex items-center justify-between mb-3">
            <p className="font-semibold text-gray-800 text-sm">Personal Details</p>
            {!editing && (
              <button onClick={startEdit} className="flex items-center gap-1 text-blue-600 text-xs font-medium">
                <Pencil size={13} /> Edit
              </button>
            )}
          </div>
          {editing ? (
            <div className="space-y-3">
              {userProfile?.userType === "student" && (() => {
                const changes = userProfile?.collegeChanges || 0;
                const approved = userProfile?.collegeChangeApproved || false;
                const canChange = changes < 2 || approved;
                const remaining = Math.max(0, 2 - changes);
                return (
                  <div>
                    <label className="text-xs text-gray-500 font-medium">College / Institution</label>
                    {canChange ? (
                      <>
                        <select value={newCollege} onChange={e => setNewCollege(e.target.value)}
                          className="w-full border border-gray-200 rounded-xl px-3 py-2.5 text-sm mt-1 focus:ring-2 focus:ring-blue-400 bg-white">
                          <option value="">{userProfile?.college || "Select college"}</option>
                          {["IIT Bombay","IIT Delhi","IIT Madras","VNIT Nagpur","BITS Pilani","BITS Goa",
                            "Veermata Jijabai Technological Institute (VJTI)","Institute of Chemical Technology (ICT Mumbai)",
                            "Sardar Patel Institute of Technology (SPIT)","K.J. Somaiya College of Engineering",
                            "NMIMS University","D.J. Sanghvi College of Engineering","Thakur College of Engineering",
                            "Pillai College of Engineering","Terna Engineering College","Atharva College of Engineering",
                            "Pune Institute of Computer Technology (PICT)","College of Engineering Pune (COEP)",
                            "Symbiosis Institute of Technology (SIT)","MIT College of Engineering, Pune","VIT Pune",
                            "Vishwakarma Institute of Technology (VIT Pune)","Cummins College of Engineering for Women",
                            "Sinhgad College of Engineering (SCOE)","Pimpri Chinchwad College of Engineering (PCCOE)",
                            "DY Patil College of Engineering, Pune","Bharati Vidyapeeth College of Engineering, Pune",
                            "Yeshwantrao Chavan College of Engineering (YCCE)","G.H. Raisoni College of Engineering, Nagpur",
                            "K.K. Wagh College of Engineering, Nashik","Sandip University, Nashik",
                            "MGM College of Engineering, Aurangabad","Shivaji University, Kolhapur",
                            "Walchand College of Engineering, Sangli","Manipal Institute of Technology",
                            "SRM Institute of Science and Technology","Amity University","Other"].map(c =>
                            <option key={c} value={c}>{c}</option>)}
                        </select>
                        {newCollege === "Other" && (
                          <input value={otherCollegeInput} onChange={e => setOtherCollegeInput(e.target.value)}
                            placeholder="Enter your college name"
                            className="w-full border border-blue-300 rounded-xl px-3 py-2.5 text-sm mt-2 focus:ring-2 focus:ring-blue-400" />
                        )}
                        <p className="text-xs text-amber-600 mt-1">
                          {approved && remaining === 2
                            ? "Admin approved — 2 changes available. Choose carefully."
                            : `${remaining} free change${remaining !== 1 ? "s" : ""} remaining. After that, contact support.`}
                        </p>
                      </>
                    ) : (
                      <div>
                        <div className="w-full border border-gray-100 bg-gray-50 rounded-xl px-3 py-2.5 text-sm mt-1 flex items-center justify-between">
                          <span className="text-gray-600 truncate">{userProfile?.college || "—"}</span>
                          <span className="text-xs text-gray-400 flex items-center gap-1 ml-2 flex-shrink-0"><Lock size={11} /> Locked</span>
                        </div>
                        <p className="text-xs text-red-500 mt-1">You've used all free changes. Submit a request below for admin approval (2 more chances if approved).</p>
                        {!collegeTicketSent ? (
                          <div className="mt-2 space-y-2">
                            <textarea value={collegeTicketMsg} onChange={e => setCollegeTicketMsg(e.target.value)}
                              placeholder="Why do you need to change your college? (e.g. transferred, wrong college entered)"
                              rows={3} className="w-full border border-gray-200 rounded-xl px-3 py-2 text-xs focus:ring-2 focus:ring-blue-400 resize-none" />
                            <button onClick={submitCollegeTicket} disabled={!collegeTicketMsg.trim()}
                              className="w-full bg-blue-600 text-white py-2 rounded-xl text-xs font-semibold disabled:opacity-50">
                              Submit Change Request to Admin
                            </button>
                          </div>
                        ) : (
                          <p className="text-xs text-green-600 mt-2 bg-green-50 rounded-lg px-3 py-2">✅ Request submitted! Admin will review and approve if valid.</p>
                        )}
                      </div>
                    )}
                  </div>
                );
              })()}
              <div>
                <label className="text-xs text-gray-500 font-medium">Full Name</label>
                <input
                  value={newName}
                  onChange={e => setNewName(e.target.value)}
                  className="w-full border border-gray-200 rounded-xl px-3 py-2.5 text-sm mt-1 focus:ring-2 focus:ring-blue-400 focus:border-transparent"
                  placeholder="Your full name"
                />
              </div>
              <div>
                <label className="text-xs text-gray-500 font-medium">Mobile Number</label>
                <input
                  value={newPhone}
                  onChange={e => setNewPhone(e.target.value)}
                  type="tel"
                  maxLength={10}
                  className="w-full border border-gray-200 rounded-xl px-3 py-2.5 text-sm mt-1 focus:ring-2 focus:ring-blue-400 focus:border-transparent"
                  placeholder="10-digit mobile number"
                />
              </div>
              <div>
                <label className="text-xs text-gray-500 font-medium">UPI ID <span className="text-gray-400 font-normal">(shown to chat partners for payments)</span></label>
                <input
                  value={upiId}
                  onChange={e => setUpiId(e.target.value)}
                  className="w-full border border-gray-200 rounded-xl px-3 py-2.5 text-sm mt-1 focus:ring-2 focus:ring-blue-400 focus:border-transparent"
                  placeholder="yourname@upi or 9999999999@paytm"
                />
              </div>
              <div className="flex gap-2">
                <button onClick={saveEdit} disabled={saving}
                  className="flex-1 bg-blue-600 text-white py-2.5 rounded-xl text-sm font-semibold flex items-center justify-center gap-1.5 disabled:opacity-50">
                  <Check size={15} /> {saving ? "Saving..." : "Save Changes"}
                </button>
                <button onClick={() => setEditing(false)}
                  className="flex-1 bg-gray-100 text-gray-600 py-2.5 rounded-xl text-sm font-semibold flex items-center justify-center gap-1.5">
                  <X size={15} /> Cancel
                </button>
              </div>
            </div>
          ) : (
            <div className="space-y-2">
              <div className="flex justify-between text-sm">
                <span className="text-gray-500">Name</span>
                <span className="font-medium text-gray-800">{userProfile?.name || "—"}</span>
              </div>
              <div className="flex justify-between text-sm">
                <span className="text-gray-500">Mobile</span>
                <span className="font-medium text-gray-800">{userProfile?.phone || "Not set"}</span>
              </div>
              <div className="flex justify-between text-sm">
                <span className="text-gray-500">Email</span>
                <span className="font-medium text-gray-800 text-xs">{userProfile?.email || "—"}</span>
              </div>
              {userProfile?.upiId && (
                <div className="flex justify-between text-sm">
                  <span className="text-gray-500">UPI ID</span>
                  <span className="font-medium text-gray-800 text-xs">{userProfile.upiId}</span>
                </div>
              )}
              {saved && <p className="text-green-600 text-xs text-center font-medium">Saved successfully!</p>}
            </div>
          )}
        </div>

        {/* Verification Banner - only for students */}
        {!isParent && !isVerified && (
          <div className={`rounded-xl p-4 flex items-start gap-3 ${verificationStatus === "pending" ? "bg-yellow-50 border border-yellow-200" : "bg-orange-50 border border-orange-200"}`}>
            <Shield size={22} className={verificationStatus === "pending" ? "text-yellow-500 flex-shrink-0 mt-0.5" : "text-orange-400 flex-shrink-0 mt-0.5"} />
            <div className="flex-1">
              <p className="font-semibold text-sm text-gray-800">
                {verificationStatus === "pending" ? "Verification Pending" : "Not Verified"}
              </p>
              <p className="text-xs text-gray-500 mt-0.5">
                {verificationStatus === "pending"
                  ? "Your college ID is under review. You'll be verified within 24 hours."
                  : "Upload your college ID to get a verified badge and build trust."}
              </p>
            </div>
            {verificationStatus !== "pending" && (
              <Link to="/verify-id" className="bg-blue-600 text-white text-xs px-3 py-1.5 rounded-lg font-medium flex-shrink-0">
                Verify Now
              </Link>
            )}
          </div>
        )}

        {/* Saved Items */}
        {savedListings.length > 0 && (
          <div className="bg-white rounded-2xl shadow-sm p-4">
            <div className="flex items-center gap-2 mb-3">
              <Heart size={16} className="text-red-500 fill-red-500" />
              <p className="font-semibold text-gray-800 text-sm">Saved Items ({savedListings.length})</p>
            </div>
            <div className="space-y-2">
              {savedListings.slice(0, 3).map(l => (
                <Link key={l.id} to={`/listing/${l.id}`} className="flex items-center gap-3 hover:bg-gray-50 rounded-xl p-2">
                  <div className="w-12 h-12 rounded-xl bg-gray-100 overflow-hidden flex-shrink-0">
                    {l.imageUrl ? <img src={l.imageUrl} alt={l.title} className="w-full h-full object-cover" /> : <div className="w-full h-full flex items-center justify-center text-xl">📦</div>}
                  </div>
                  <div className="flex-1 min-w-0">
                    <p className="text-sm font-medium text-gray-800 line-clamp-1">{l.title}</p>
                    <p className="text-xs text-blue-600 font-bold">{l.price === 0 ? "Negotiable" : `₹${l.price}`}</p>
                  </div>
                  <ChevronRight size={14} className="text-gray-300" />
                </Link>
              ))}
              {savedListings.length > 3 && (
                <p className="text-xs text-blue-600 text-center font-medium pt-1">+{savedListings.length - 3} more saved items</p>
              )}
            </div>
          </div>
        )}

        {/* Referral */}
        {userProfile?.referralCode && (
          <div className="bg-gradient-to-r from-blue-600 to-indigo-600 rounded-2xl p-4 text-white">
            <p className="font-bold text-sm mb-1">🎁 Invite Friends — Earn Faster Verification</p>
            <p className="text-xs text-blue-200 mb-3">Share your code. Both you & your friend get verified faster!</p>
            <div className="flex items-center gap-2">
              <div className="bg-white/20 rounded-xl px-4 py-2 flex-1 text-center font-bold text-lg tracking-widest">
                {userProfile.referralCode}
              </div>
              <button onClick={() => {
                navigator.clipboard.writeText(`Join CampusSwap! Use my code ${userProfile.referralCode} to get verified faster. ${window.location.origin}`);
                setReferralCopied(true);
                setTimeout(() => setReferralCopied(false), 2000);
              }} className="bg-white text-blue-600 px-4 py-2 rounded-xl font-semibold text-sm flex items-center gap-1">
                {referralCopied ? "✓ Copied" : <><Copy size={14} /> Copy</>}
              </button>
            </div>
          </div>
        )}

        {/* Menu */}
        <div className="bg-white rounded-2xl shadow-sm overflow-hidden">
          <button onClick={() => setShowMenu(m => !m)}
            className="flex items-center gap-3 px-4 py-3.5 w-full text-left hover:bg-gray-50">
            <Package size={18} className="text-blue-500" />
            <span className="text-sm font-semibold text-gray-800">My Activity</span>
            <ChevronRight size={16} className={`ml-auto text-gray-400 transition-transform duration-200 ${showMenu ? "rotate-90" : ""}`} />
          </button>
          {showMenu && (
            <div className="border-t border-gray-50">
              {(isParent ? [
                { to: "/parent-zone", icon: Users, label: "Parent Zone", color: "text-amber-500" },
                { to: "/notice-board", icon: Bell, label: "Notice Board", color: "text-purple-500" },
              ] : [
                { to: "/my-listings", icon: Package, label: "My Listings", color: "text-blue-500" },
                { to: "/rental-bookings", icon: CalendarDays, label: "Rental Bookings", color: "text-teal-500" },
                { to: "/transactions", icon: History, label: "Transaction History", color: "text-purple-500" },
                { to: "/chat", icon: MessageCircle, label: "My Chats", color: "text-green-500" },
                { to: "/leaderboard", icon: Trophy, label: "Leaderboard", color: "text-yellow-500" },
                { to: "/referrals", icon: Users, label: "My Referrals", color: "text-indigo-500" },
                { to: "/book-exchange", icon: FileText, label: "Book Exchange", color: "text-orange-500" },
                { to: "/swap", icon: ArrowLeftRight, label: "My Swaps", color: "text-indigo-500" },
                { to: "/notes", icon: FileText, label: "My Notes", color: "text-yellow-500" },
                { to: "/notice-board", icon: Bell, label: "Notice Board", color: "text-purple-500" },
                { to: "/ambassador", icon: TrendingUp, label: "Campus Ambassador", color: "text-indigo-500" },
              ]).map(({ to, icon: Icon, label, color }, i, arr) => (
                <Link key={to} to={to}
                  className={`flex items-center justify-between px-4 py-3 hover:bg-gray-50 ${i < arr.length - 1 ? "border-b border-gray-50" : ""}`}>
                  <div className="flex items-center gap-3">
                    <Icon size={16} className={color} />
                    <span className="text-sm text-gray-700">{label}</span>
                  </div>
                  <ChevronRight size={14} className="text-gray-300" />
                </Link>
              ))}
            </div>
          )}
        </div>

        {/* College Email Verification */}
        {!isParent && !userProfile?.collegeEmailVerified && (
          <div className="bg-white rounded-2xl shadow-sm p-4">
            {showEmailVerify ? (
              <div>
                <div className="flex items-center gap-2 mb-3">
                  <Mail size={16} className="text-blue-500" />
                  <p className="font-semibold text-gray-800 text-sm">Verify with College Email</p>
                </div>
                <p className="text-xs text-gray-500 mb-3">Enter your college email (.edu, .ac.in, etc.). Our team will verify and approve within 24 hours.</p>
                <input
                  type="email"
                  value={collegeEmail}
                  onChange={e => setCollegeEmail(e.target.value)}
                  placeholder="yourname@college.ac.in"
                  className="w-full border border-gray-200 rounded-xl px-3 py-2.5 text-sm mb-3 focus:ring-2 focus:ring-blue-400 focus:border-transparent"
                />
                <div className="flex gap-2">
                  <button onClick={verifyCollegeEmail} disabled={emailVerifying || !collegeEmail.trim()}
                    className="flex-1 bg-blue-600 text-white py-2.5 rounded-xl text-sm font-semibold flex items-center justify-center gap-1.5 disabled:opacity-50">
                    <CheckCircle size={14} /> {emailVerifying ? "Submitting..." : "Submit for Review"}
                  </button>
                  <button onClick={() => setShowEmailVerify(false)}
                    className="flex-1 bg-gray-100 text-gray-600 py-2.5 rounded-xl text-sm font-semibold">
                    Cancel
                  </button>
                </div>
              </div>
            ) : (
              <button onClick={() => setShowEmailVerify(true)}
                className="flex items-center gap-3 w-full hover:bg-gray-50 rounded-xl p-1">
                <div className="w-9 h-9 bg-blue-100 rounded-xl flex items-center justify-center flex-shrink-0">
                  <Mail size={16} className="text-blue-600" />
                </div>
                <div className="text-left">
                  <p className="text-sm font-semibold text-gray-800">Verify with College Email</p>
                  <p className="text-xs text-gray-500">Use your .edu or .ac.in email — verified within 24 hours</p>
                </div>
              </button>
            )}
          </div>
        )}
        {userProfile?.collegeEmailVerified && (
          <div className="bg-green-50 border border-green-200 rounded-2xl p-3 flex items-center gap-3">
            <CheckCircle size={18} className="text-green-600 flex-shrink-0" />
            <div>
              <p className="text-sm font-semibold text-green-800">College Email Verified</p>
              <p className="text-xs text-green-600">{userProfile.collegeEmail}</p>
            </div>
          </div>
        )}

        {/* Change Password — only for email users */}
        {currentUser?.email && (
          <div className="bg-white rounded-2xl shadow-sm overflow-hidden">
            <button onClick={() => { setShowPwSection(!showPwSection); setPwError(""); setPwSuccess(false); }}
              className="flex items-center gap-3 px-4 py-3.5 w-full text-left hover:bg-gray-50">
              <Lock size={18} className="text-blue-400" />
              <span className="text-sm font-medium text-gray-700">Change Password</span>
              <ChevronRight size={16} className={`ml-auto text-gray-400 transition-transform ${showPwSection ? "rotate-90" : ""}`} />
            </button>
            {showPwSection && (
              <div className="px-4 pb-4 border-t border-gray-100">
                {pwSuccess ? (
                  <div className="flex items-center gap-2 bg-green-50 border border-green-200 text-green-700 rounded-xl px-3 py-3 mt-3 text-sm">
                    <CheckCircle size={16} /> Password changed successfully!
                  </div>
                ) : (
                  <form onSubmit={handleChangePassword} className="space-y-3 mt-3">
                    {pwError && <p className="text-red-500 text-xs bg-red-50 border border-red-200 rounded-xl px-3 py-2">{pwError}</p>}
                    {[
                      { key: "current", label: "Current Password" },
                      { key: "newPw",   label: "New Password" },
                      { key: "confirm", label: "Confirm New Password" },
                    ].map(({ key, label }) => (
                      <div key={key}>
                        <label className="block text-xs font-medium text-gray-500 mb-1">{label}</label>
                        <div className="relative">
                          <Lock size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" />
                          <input
                            type={showPw[key] ? "text" : "password"}
                            value={pwForm[key]}
                            onChange={e => setPwForm(f => ({ ...f, [key]: e.target.value }))}
                            placeholder={key === "newPw" ? "Min 8 chars, mix of letters & numbers" : ""}
                            className="w-full pl-9 pr-9 py-2.5 border border-gray-200 rounded-xl text-sm focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                            required
                          />
                          <button type="button" onClick={() => setShowPw(s => ({ ...s, [key]: !s[key] }))}
                            className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400">
                            {showPw[key] ? <EyeOff size={14} /> : <Eye size={14} />}
                          </button>
                        </div>
                        {key === "newPw" && pwForm.newPw.length > 0 && (() => {
                          const s = getPwStrength(pwForm.newPw);
                          return (
                            <div className="mt-1.5 px-1">
                              <div className="flex gap-1 mb-0.5">{[1,2,3,4].map(i => <div key={i} className={`h-1 flex-1 rounded-full ${s.score > i ? s.color : "bg-gray-200"}`} />)}</div>
                              <p className={`text-xs font-medium ${s.score <= 2 ? "text-red-500" : s.score === 3 ? "text-yellow-600" : s.score === 4 ? "text-blue-600" : "text-green-600"}`}>{s.label}</p>
                            </div>
                          );
                        })()}
                      </div>
                    ))}
                    <button type="submit" disabled={pwLoading}
                      className="w-full bg-blue-600 text-white py-2.5 rounded-xl font-semibold text-sm hover:bg-blue-700 disabled:opacity-60">
                      {pwLoading ? "Changing..." : "Change Password"}
                    </button>
                  </form>
                )}
              </div>
            )}
          </div>
        )}

        {/* Photo Privacy Settings */}
        <div className="bg-white rounded-2xl shadow-sm overflow-hidden">
          <button onClick={() => setShowPrivacySection(s => !s)}
            className="flex items-center gap-3 px-4 py-3.5 w-full text-left hover:bg-gray-50">
            <Eye size={18} className="text-purple-500" />
            <div className="flex-1 text-left">
              <span className="text-sm font-medium text-gray-700">Photo Privacy</span>
              <p className="text-xs text-gray-400 mt-0.5">
                {photoPrivacy === "everyone" && "Visible to everyone"}
                {photoPrivacy === "contacts" && "Only contacts can see"}
                {photoPrivacy === "nobody" && "Hidden from everyone"}
                {photoPrivacy === "custom" && `Custom list (${Object.keys(photoAllowList).length} allowed)`}
              </p>
            </div>
            <ChevronRight size={16} className={`text-gray-400 transition-transform ${showPrivacySection ? "rotate-90" : ""}`} />
          </button>
          {showPrivacySection && (
            <div className="px-4 pb-4 border-t border-gray-100 space-y-3 mt-3">
              <p className="text-xs text-gray-500">Choose who can see your profile photo across listings, chats, and seller profiles.</p>
              {[
                { value: "everyone", icon: "👁", label: "Everyone", desc: "Default — all users can see your photo" },
                { value: "contacts", icon: "👥", label: "Only contacts", desc: "People you've chatted with" },
                { value: "nobody", icon: "🔒", label: "Nobody", desc: "Your photo is hidden everywhere" },
                { value: "custom", icon: "✏️", label: "Custom list", desc: "Only specific users you allow" },
              ].map(opt => (
                <button key={opt.value}
                  onClick={() => { if (!savingPrivacy) savePrivacy(opt.value); }}
                  className={`w-full flex items-center gap-3 px-3 py-3 rounded-xl border-2 transition-all text-left ${photoPrivacy === opt.value ? "border-purple-500 bg-purple-50" : "border-gray-100 bg-gray-50 hover:border-gray-200"}`}>
                  <span className="text-lg">{opt.icon}</span>
                  <div className="flex-1">
                    <p className={`text-sm font-semibold ${photoPrivacy === opt.value ? "text-purple-700" : "text-gray-700"}`}>{opt.label}</p>
                    <p className="text-xs text-gray-400">{opt.desc}</p>
                  </div>
                  {photoPrivacy === opt.value && <div className="w-5 h-5 bg-purple-500 rounded-full flex items-center justify-center flex-shrink-0"><Check size={12} color="white" /></div>}
                </button>
              ))}
              {privacySaved && <p className="text-xs text-green-600 font-medium text-center">Saved!</p>}
              {savingPrivacy && <p className="text-xs text-gray-400 text-center">Saving...</p>}

              {/* Custom allow list */}
              {photoPrivacy === "custom" && (
                <div className="pt-1 border-t border-gray-100 space-y-2">
                  <p className="text-xs font-semibold text-gray-700">Allowed users ({Object.keys(photoAllowList).length})</p>
                  {Object.entries(photoAllowList).map(([uid, u]) => (
                    <div key={uid} className="flex items-center gap-2 bg-gray-50 rounded-xl px-3 py-2">
                      <div className="w-8 h-8 rounded-full bg-purple-100 overflow-hidden flex-shrink-0 flex items-center justify-center text-sm font-bold text-purple-600">
                        {u.photoURL ? <img src={u.photoURL} alt={u.name} className="w-full h-full object-cover" /> : u.name?.[0]?.toUpperCase()}
                      </div>
                      <span className="text-sm text-gray-700 flex-1 truncate">{u.name}</span>
                      <button onClick={() => removeFromAllowList(uid)} className="text-red-400 hover:text-red-600 p-1">
                        <X size={14} />
                      </button>
                    </div>
                  ))}
                  {Object.keys(photoAllowList).length === 0 && (
                    <p className="text-xs text-gray-400 italic">No users added yet. Search below to add.</p>
                  )}
                  <div className="relative mt-2">
                    <input
                      value={allowSearch}
                      onChange={e => { setAllowSearch(e.target.value); searchAllowUsers(e.target.value); }}
                      placeholder="Search by name or college..."
                      className="w-full border border-gray-200 rounded-xl px-3 py-2.5 text-sm focus:ring-2 focus:ring-purple-400 focus:border-transparent"
                    />
                    {searchingAllow && <div className="absolute right-3 top-1/2 -translate-y-1/2 w-4 h-4 border-2 border-purple-400 border-t-transparent rounded-full animate-spin" />}
                  </div>
                  {allowSearchResults.length > 0 && (
                    <div className="border border-gray-200 rounded-xl overflow-hidden divide-y divide-gray-100">
                      {allowSearchResults.map(u => (
                        <button key={u.uid} onClick={() => addToAllowList(u)}
                          className="flex items-center gap-2 w-full px-3 py-2.5 hover:bg-purple-50 text-left">
                          <div className="w-8 h-8 rounded-full bg-gray-100 overflow-hidden flex-shrink-0 flex items-center justify-center text-sm font-bold text-gray-500">
                            {u.photoURL ? <img src={u.photoURL} alt={u.name} className="w-full h-full object-cover" /> : u.name?.[0]?.toUpperCase()}
                          </div>
                          <div className="flex-1 min-w-0">
                            <p className="text-sm font-medium text-gray-800 truncate">{u.name}</p>
                            {u.college && <p className="text-xs text-gray-400 truncate">{u.college}</p>}
                          </div>
                          <span className="text-xs text-purple-600 font-medium flex-shrink-0">+ Add</span>
                        </button>
                      ))}
                    </div>
                  )}
                </div>
              )}
            </div>
          )}
        </div>

        {/* Logout */}
        <div className="bg-white rounded-2xl shadow-sm overflow-hidden">
          <button onClick={handleLogout}
            className="flex items-center gap-3 px-4 py-3.5 w-full text-left hover:bg-gray-50">
            <LogOut size={18} className="text-red-400" />
            <span className="text-sm font-medium text-red-500">Logout</span>
          </button>
        </div>

        {/* Help & Support */}
        <div className="bg-white rounded-2xl shadow-sm overflow-hidden">
          <button onClick={() => { setShowFeedback(!showFeedback); setFeedbackDone(false); }}
            className="flex items-center gap-3 px-4 py-3.5 w-full text-left hover:bg-blue-50">
            <HelpCircle size={18} className="text-blue-500" />
            <span className="text-sm font-medium text-gray-700">Help & Support</span>
            <ChevronRight size={16} className={`ml-auto text-gray-400 transition-transform ${showFeedback ? "rotate-90" : ""}`} />
          </button>
          {showFeedback && (
            <div className="px-4 pb-4 border-t border-gray-100">
              <p className="text-xs text-gray-500 mt-3 mb-3">
                Have a complaint, bug, or suggestion? We read every message.
                Or email us directly at{" "}
                <a href="mailto:campusswap.official@gmail.com" className="text-blue-600 font-medium">campusswap.official@gmail.com</a>
              </p>
              {feedbackDone ? (
                <div className="bg-green-50 border border-green-200 text-green-700 text-sm px-4 py-3 rounded-xl font-medium text-center">
                  ✅ Thanks! We'll get back to you soon.
                </div>
              ) : (
                <form onSubmit={submitFeedback} className="space-y-3">
                  <div>
                    <label className="block text-xs font-medium text-gray-500 mb-1">Type</label>
                    <select value={feedbackType} onChange={e => setFeedbackType(e.target.value)}
                      className="w-full border border-gray-200 rounded-xl px-3 py-2.5 text-sm focus:ring-2 focus:ring-blue-400 focus:border-transparent bg-white">
                      <option>Bug Report</option>
                      <option>Complaint</option>
                      <option>Suggestion</option>
                      <option>Other</option>
                    </select>
                  </div>
                  <div>
                    <label className="block text-xs font-medium text-gray-500 mb-1">Message *</label>
                    <textarea value={feedbackMsg} onChange={e => setFeedbackMsg(e.target.value)}
                      placeholder="Describe your issue or suggestion..."
                      rows={4}
                      required
                      className="w-full border border-gray-200 rounded-xl px-3 py-2.5 text-sm focus:ring-2 focus:ring-blue-400 focus:border-transparent resize-none" />
                  </div>
                  {feedbackError && (
                    <p className="text-xs text-red-600 bg-red-50 rounded-lg px-3 py-2">{feedbackError}</p>
                  )}
                  <button type="submit" disabled={feedbackSubmitting || !feedbackMsg.trim()}
                    className="w-full bg-blue-600 text-white py-2.5 rounded-xl font-semibold text-sm hover:bg-blue-700 disabled:opacity-50">
                    {feedbackSubmitting ? "Sending..." : "Send Message"}
                  </button>
                </form>
              )}
            </div>
          )}
        </div>

        {/* Delete Account */}
        <div className="bg-white rounded-2xl shadow-sm overflow-hidden">
          <button onClick={() => { setShowDeleteSection(!showDeleteSection); setDeleteError(""); setDeleteConfirmText(""); setDeletePassword(""); }}
            className="flex items-center gap-3 px-4 py-3.5 w-full text-left hover:bg-red-50">
            <Trash2 size={18} className="text-red-500" />
            <span className="text-sm font-medium text-red-600">Delete Account</span>
            <ChevronRight size={16} className={`ml-auto text-gray-400 transition-transform ${showDeleteSection ? "rotate-90" : ""}`} />
          </button>
          {showDeleteSection && (
            <div className="px-4 pb-4 border-t border-red-100">
              <div className="flex items-start gap-2 bg-red-50 border border-red-200 rounded-xl p-3 mt-3 mb-3">
                <AlertTriangle size={15} className="text-red-500 flex-shrink-0 mt-0.5" />
                <p className="text-xs text-red-700">This will permanently delete your account, all listings, and saved data. This cannot be undone.</p>
              </div>
              <form onSubmit={handleDeleteAccount} className="space-y-3">
                {deleteError && <p className="text-red-500 text-xs bg-red-50 border border-red-200 rounded-xl px-3 py-2">{deleteError}</p>}
                {currentUser?.email && (
                  <div>
                    <label className="block text-xs font-medium text-gray-500 mb-1">Confirm Password</label>
                    <input type="password" value={deletePassword} onChange={e => setDeletePassword(e.target.value)}
                      placeholder="Your account password"
                      className="w-full border border-gray-200 rounded-xl px-3 py-2.5 text-sm focus:ring-2 focus:ring-red-400 focus:border-transparent"
                      required />
                  </div>
                )}
                <div>
                  <label className="block text-xs font-medium text-gray-500 mb-1">Type DELETE to confirm</label>
                  <input type="text" value={deleteConfirmText} onChange={e => setDeleteConfirmText(e.target.value)}
                    placeholder="DELETE"
                    className="w-full border border-gray-200 rounded-xl px-3 py-2.5 text-sm focus:ring-2 focus:ring-red-400 focus:border-transparent"
                    required />
                </div>
                <button type="submit" disabled={deleteLoading || deleteConfirmText !== "DELETE"}
                  className="w-full bg-red-600 text-white py-2.5 rounded-xl font-semibold text-sm hover:bg-red-700 disabled:opacity-50 flex items-center justify-center gap-2">
                  <Trash2 size={15} /> {deleteLoading ? "Deleting..." : "Permanently Delete My Account"}
                </button>
              </form>
            </div>
          )}
        </div>

        <div className="text-center pb-2 space-y-1">
          <div className="flex justify-center gap-3">
            <Link to="/privacy-policy" className="text-xs text-gray-400 hover:underline">Privacy Policy</Link>
            <span className="text-gray-300 text-xs">·</span>
            <Link to="/terms" className="text-xs text-gray-400 hover:underline">Terms &amp; Conditions</Link>
          </div>
          <p className="text-xs text-gray-300">CampusSwap v1.0 · Made with ❤️ in India</p>
        </div>
      </div>

      {/* Full-screen photo viewer */}
      {showPhotoViewer && userProfile?.photoURL && (
        <div
          onClick={() => setShowPhotoViewer(false)}
          style={{ position: "fixed", inset: 0, background: "rgba(0,0,0,0.92)", zIndex: 9999, display: "flex", alignItems: "center", justifyContent: "center" }}
        >
          <button
            onClick={() => setShowPhotoViewer(false)}
            style={{ position: "absolute", top: 16, right: 16, background: "rgba(255,255,255,0.15)", border: "none", borderRadius: "9999px", width: 40, height: 40, display: "flex", alignItems: "center", justifyContent: "center", cursor: "pointer" }}
          >
            <X size={20} color="white" />
          </button>
          <img
            src={userProfile.photoURL}
            alt="Profile"
            onClick={e => e.stopPropagation()}
            style={{ maxWidth: "92vw", maxHeight: "92vh", borderRadius: 16, objectFit: "contain", boxShadow: "0 8px 40px rgba(0,0,0,0.6)" }}
          />
        </div>
      )}
    </div>
  );
}
